import React, { FC, createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useParams } from "react-router";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { TransferApi } from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import {
	TransactionContext,
	TransactionResourceType,
} from "../../../../../../components/Transaction/Context/TransactionContext";
import TransferPreview from "../../components/TransferPreview/TransferPreview";

interface MatchParams {
	transferId: string;
}

interface TransferContextType {
	transfer: TransferApi;
	setTransfer: (transfer: TransferApi) => void;
	onFetchTransfer: () => void;
	customFieldsConfig?: CustomFieldTemplateApi[];
}

export const TransferContext = createContext<TransferContextType>({
	transfer: {} as TransferApi,
	setTransfer: (transfer: TransferApi) => {},
	onFetchTransfer: () => {},
	customFieldsConfig: [] as CustomFieldTemplateApi[],
});

const OrganizationTransferPreviewPage: FC<RouteComponentProps> = (props) => {
	const [transfer, setTransfer] = useState<TransferApi>({} as TransferApi);
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>(undefined);
	const { addFlash } = useFlash();
	const { transferId } = useParams<MatchParams>();
	const { setListItemId, setTransactionResourceType } = useContext(TransactionContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation();

	useEffect(() => {
		setListItemId(transferId);
		setTransactionResourceType(TransactionResourceType.TRANSFER);
		onFetchTransfer();
	}, []);

	const onFetchTransfer = async () => {
		try {
			const params = { include: "all" };
			const customFieldsParams = {
				f: btoa(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}resource_type|e=TRANSFER_ORDER`),
			};

			const requests: [Promise<TransferApi>, Promise<CustomFieldTemplateApi[]>] = [
				api.organization().getTransfer(Number(transferId), params),
				api.organization().getCustomFields(customFieldsParams),
			];
			const [newTransfer, newCustomFieldsConfig] = await Promise.all(requests);
			setTransfer(newTransfer);
			setCustomFieldsConfig(newCustomFieldsConfig);
			handleChangeTabTitle(`${newTransfer.document_number} | ${t("modules.transfer.header.title")}`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<TransferContext.Provider
			value={{
				transfer,
				setTransfer,
				onFetchTransfer,
				customFieldsConfig,
			}}
		>
			<TransferPreview path={props.match.path} />
		</TransferContext.Provider>
	);
};

export default OrganizationTransferPreviewPage;
